import type { InventoryType } from '@/enums/inventory-type';
import type {
    ItemDataItemArray,
    DataItemBonusArray,
    DataItemSetArray,
    ItemDataItem,
    DataItemBonus,
    DataItemSet,
} from '@/types/data/item';
import type {
    DataCurve,
    DataCurveArray,
    DataItemOffsetCurve,
    DataItemOffsetCurveArray,
    DataItemScalingConfig,
    DataItemScalingConfigArray,
} from '@/types/data/item/curve';
import type { DataItemModifiedCrafting } from '@/types/data/item/modified-crafting';

export interface RawItems {
    classIdSubclassIdInventoryTypes: [number, number, number][];
    classMasks: number[];
    names: string[];
    oppositeFactionIds: number[];
    raceMasks: number[];

    appearanceMap: Record<number, number>;
    bonusIdToModifiedCrafting: Record<number, DataItemModifiedCrafting>;
    completesQuest: Record<number, number[]>;
    craftingQualities: Record<number, number[]>;
    itemBonusListGroups: Record<number, Record<number, number[]>>;
    itemConversionEntries: Record<number, number[]>;
    itemRequiredSkills: Record<number, [number, number]>;
    itemSquishEras: Record<number, number>;
    limitCategories: Record<number, number>;
    limitCategoryItems: Record<number, number[]>;
    specOverrides: Record<number, number[]>;
    teachesDecor: Record<number, number[]>;
    teachesIllusion: Record<number, number[]>;
    teachesSpell: Record<number, number[]>;
    teachesTransmog: Record<number, number>;

    rawCurves: DataCurveArray[];
    rawItemOffsetCurves: DataItemOffsetCurveArray[];
    rawItemScalingConfigs: DataItemScalingConfigArray[];

    rawItems: ItemDataItemArray[];
    rawItemBonuses: DataItemBonusArray[];
    rawItemSets: DataItemSetArray[];
}

export class DataItems {
    public appearanceMap: RawItems['appearanceMap'];
    public bonusIdToModifiedCrafting: RawItems['bonusIdToModifiedCrafting'];
    public completesQuest: RawItems['completesQuest'];
    public itemConversionEntries: RawItems['itemConversionEntries'];
    public itemRequiredSkills: RawItems['itemRequiredSkills'];
    public itemSquishEras: RawItems['itemSquishEras'];
    public limitCategories: RawItems['limitCategories'];
    public specOverrides: RawItems['specOverrides'];
    public teachesDecor: RawItems['teachesDecor'];
    public teachesIllusion: RawItems['teachesIllusion'];
    public teachesSpell: RawItems['teachesSpell'];
    public teachesTransmog: RawItems['teachesTransmog'];

    public appearanceToItems: Record<number, [number, number][]> = {};
    public items: Record<number, ItemDataItem> = {};
    public itemBonuses: Record<number, DataItemBonus> = {};
    public itemBonusAvoidance: Set<number> = new Set();
    public itemBonusCurrentSeason: Set<number> = new Set();
    public itemBonusLeech: Set<number> = new Set();
    public itemBonusSocket: Set<number> = new Set();
    public itemBonusSpeed: Set<number> = new Set();
    public itemBonusToUpgrade: Record<number, [number, number, number]> = {};
    public itemConversionBonus: Record<number, number> = {};
    public itemSets: Record<number, DataItemSet> = {};
    public openableItemIds: Set<number> = new Set();
    public oppositeFactionAppearance: Record<number, number[]>;
    public transmogSetToItems: Record<number, number[]> = {};

    public curveById = new Map<number, DataCurve>();
    public itemOffsetCurveById = new Map<number, DataItemOffsetCurve>();
    public itemScalingConfigById = new Map<number, DataItemScalingConfig>();

    public currentTier: Record<number, InventoryType>;
    public previousTier: Record<number, InventoryType>;
}
